import { createFileRoute } from "@tanstack/react-router";

import { createHead } from "@/components/createHead";
import { Picture } from "@/components/Picture";
import andrew from "@/images/andrew.jpeg?w=500;900;1200&format=avif;webp;jpg&as=picture";
import carrot from "@/images/carrot.png?w=500;900;1200&format=avif;webp;jpg&as=picture";
import devin from "@/images/devin.jpeg?w=500;900;1200&format=avif;webp;jpg&as=picture";
// @ts-expect-error Dunno how to do types
import deer from "@/images/illustration.png?slice=1&height=250&width=250&x=1099&y=4731";
import spider from "@/images/spider.webp?w=500;900;1200&format=avif;webp;jpg&as=picture";
import tinyFlower from "@/images/tiny-footer-flower.webp?w=500;900;1200&format=avif;webp;jpg&as=picture";

export const Route = createFileRoute("/_marketing-header-layout/about")({
  component: RouteComponent,
  head: ({ match: { pathname } }) =>
    createHead({
      title: "About | Graze",
      description: "About Graze.social",
      ogTitle: "About",
      ogImage: "/about/og.png",
      pathname,
    }),
});

function RouteComponent() {
  return (
    <>
      <div
        className="absolute left-0 top-0 right-0 h-[30vh] overflow-hidden z-[-1] about-header"
        style={{
          backgroundImage: "url('/about.png')",
          backgroundSize: "cover",
          backgroundPosition: "50% 60%",
        }}
      >
        <div className="absolute inset-0 bg-linear-to-b from-10% to-95% from-transparent to-slatedark-1"></div>
      </div>
      <div className="text-slatedark-12 w-screen md:w-auto md:max-w-304 min-h-screen mx-auto pt-14 md:pt-14 pb-20 flex flex-col gap-6 md:gap-8 relative z-0 instrument-sans">
        <div className="flex flex-col gap-16 md:gap-20">
          <div className="pt-10 pb-4 md:pb-8 flex flex-col gap-6 @container px-4 md:px-10 xl:px-16">
            <h1
              className="mt-4 text-[min(14cqw,6rem)] font-bold instrument-serif text-center leading-none"
              style={{
                textShadow: "0 0 10px rgba(255, 255, 255, 0.8)",
              }}
            >
              Shaping the
              <br />
              modern social web
            </h1>

            <p className="text-xl md:text-2xl max-w-[60ch] mx-auto text-center text-slatedark-11 leading-relaxed">
              We equip developers with the tools to do more, enfranchise
              curators to shape their communities, and empower brands to own
              their algorithms. Graze is committed to fostering an open web that
              prioritizes people, creativity, and authenticity. We're putting
              the power back into the hands of those who shape it.
            </p>
          </div>
          <div className="flex justify-center">
            <div className="h-32 w-32 @container">
              <div
                className="sharp-grotesk leading-[0.9] h-full w-full rounded-full border border-slatedark-7"
                style={{
                  backgroundImage: `url('${deer}')`,
                  backgroundSize: "100cqw",
                  boxShadow: "inset 0 0 12px rgba(0, 0, 0, 0.4)",
                }}
              ></div>
            </div>
          </div>
          <section className="flex flex-col items-center gap-6 md:gap-10 px-4 md:px-10 xl:px-16">
            <h2 className="text-center text-4xl md:text-5xl font-bold instrument-serif">
              What we stand for
            </h2>

            <div className="flex flex-col md:flex-row gap-6 md:gap-10 md:pt-10">
              <div className="flex items-center flex-col gap-2">
                <div className="w-[100px] h-[100px] flex items-center justify-center py-4">
                  <Picture src={spider} alt="" />
                </div>
                <h3 className="text-xl md:text-2xl font-semibold">
                  Empowerment
                </h3>
                <p className="text-lg md:text-xl max-w-[40ch] mx-auto leading-relaxed">
                  We believe individuals have a right to control and invite
                  others into their experience online. We build tools that let
                  you create, manage, and grow your vision what social media
                  should be.
                </p>
              </div>

              <div className="flex items-center flex-col gap-2">
                <div className="w-[100px] h-[100px] flex items-center justify-center py-4">
                  <Picture src={tinyFlower} alt="" />
                </div>
                <h3 className="text-xl md:text-2xl font-semibold">
                  Authenticity
                </h3>
                <p className="text-lg md:text-xl max-w-[40ch] mx-auto leading-relaxed">
                  Algorithmic choice is predicated on algorithmic transparency.
                  Our work requires transparency. We believe in clearly
                  communicating how we work and what we do on users behalf.
                </p>
              </div>

              <div className="flex flex-col items-center gap-2">
                <div className="w-[100px] h-[100px] flex items-center justify-center py-4">
                  <Picture src={carrot} alt="" />
                </div>
                <h3 className="text-xl md:text-2xl font-semibold">Community</h3>
                <p className="text-lg md:text-xl max-w-[40ch] mx-auto leading-relaxed">
                  Communities are the bedrock of the social web. We design
                  everything for the moderators, creators, bridge-builders, and
                  community leaders who actually make online spaces thrive. When
                  they succeed, we all succeed.
                </p>
              </div>
            </div>
          </section>

          <section>
            <div className="px-4 md:px-10 xl:px-16 py-10 md:py-16 flex flex-col gap-6">
              <h2 className="text-center text-4xl md:text-5xl font-bold instrument-serif">
                How we got here
              </h2>

              <p className="text-lg md:text-xl max-w-[40ch] mx-auto leading-relaxed">
                Graze was born from years of deep work theorizing the social
                web: building tools, dissecting systems, and probing the{" "}
                <b>mechanics of influence</b>, power, and connection. We've
                spent decades thinking critically about how platforms shape
                behavior, how <b>algorithms mediate truth</b>, and how control
                becomes concentrated in the hands of a few. That history is
                baked into every part of what we're building.
              </p>

              <p className="text-lg md:text-xl max-w-[40ch] mx-auto leading-relaxed">
                Now, for the first time, the infrastructure exists to do it
                right.
                <b>
                  Bluesky and the AT Protocol make it possible to build a truly
                  open, user-owned social web
                </b>
                , and Graze is here to help shape it.
              </p>
            </div>
          </section>

          <section className="flex flex-col gap-8 px-4 md:px-10 xl:px-16 pb-10 md:pb-16">
            <div className="flex flex-col gap-4">
              <h2 className="text-center text-4xl md:text-5xl font-bold instrument-serif">
                Meet the team
              </h2>

              <p className="text-lg max-w-[40ch] mx-auto text-center text-slatedark-11">
                A fully remote team with years of experience building software
                for the open social web.
              </p>
            </div>

            <div className="flex flex-col md:flex-row justify-center items-center gap-6 px-8">
              <div className="flex flex-col items-center gap-4">
                <Picture
                  src={andrew}
                  alt="The Graze team"
                  className="max-w-[300px] mx-auto rounded-lg overflow-hidden min-w-0 w-full"
                />

                <p className="text-sm text-slatedark-11">
                  <a
                    className="underline"
                    href="https://bsky.app/profile/hipstersmoothie.com"
                  >
                    Andrew Lisowski
                  </a>
                </p>
              </div>
              <div className="flex flex-col items-center gap-4">
                <Picture
                  src={devin}
                  alt="The Graze team"
                  className="max-w-[300px] mx-auto rounded-lg overflow-hidden min-w-0 w-full"
                />
                <p className="text-sm text-slatedark-11">
                  <a
                    className="underline"
                    href="https://bsky.app/profile/devingaffney.com"
                  >
                    Devin Gaffney
                  </a>
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}
